import { authSession } from '@units/auth/service/stores';
import { type SessionState } from '@units/auth/types';

import { useSession } from './use-session.hook.js';

/**
 * What the composition root renders from, and the thing that makes `unknown` temporary.
 *
 * Two responsibilities, and they belong together: it makes sure the one `POST /auth/refresh` of
 * this tab has been started, and it re-renders whoever reads it when the answer lands. Split apart,
 * the reader could mount without the exchange ever being started — which is the failure this story
 * exists to close, and the failure with no symptom: guards let `unknown` through on purpose, so the
 * application would simply wait for ever.
 *
 * The reading half is `useSession`, and it is a hook of its own so that everything below the
 * composition root can have it **without** the acting half. A badge in the header that reads the
 * session through this hook looks identical in a browser and takes the suite apart, because an
 * `unavailable` rotation releases the memo on purpose and a reader asking on every render never
 * stops asking (`use-session.hook.ts`). One caller starts the exchange: `app/app.component.tsx`.
 *
 * The call to `bootstrap()` during render is deliberate and is not the `useEffect` that
 * `rules/frontend-fsd.mdc` rule 11 forbids — it is the opposite of it. `bootstrap()` is memoised
 * for the life of the store, so the second, third and hundredth render observe the promise the
 * first one made, `StrictMode`'s extra render included, and it changes nothing synchronously.
 * Starting it here rather than from an effect is what lets the first paint already be waiting for
 * the answer instead of starting to wait one commit later; there is nothing to clean up, because
 * there is nothing to undo.
 */
export const useBootstrapSession = (): SessionState => {
  void authSession.bootstrap();

  return useSession();
};
